import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Award } from "lucide-react";
import { scrollToId, EASE } from "@/lib/site";

const LINES = [
  [{ t: "We Design" }],
  [{ t: "Homes That" }],
  [{ t: "Feel Like " }, { t: "Home.", italic: true }],
];

const METRICS = [
  { value: "19+", label: "Years of Practice" },
  { value: "2018", label: "The Studio31 Founded" },
  { value: "2,350", label: "Sq.Ft — Knights Court" },
  { value: "4 Mo", label: "Absolute Handover" },
];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, 60]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-screen flex flex-col justify-between overflow-hidden">
      <div
        aria-hidden
        className="absolute -bottom-16 -left-4 font-serif text-[30vw] leading-none text-outline-ink select-none pointer-events-none"
      >
        31
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 w-full pt-32 lg:pt-40 pb-8 grid lg:grid-cols-12 gap-14 lg:gap-10 items-end">
        <motion.div style={{ y: yText }} className="lg:col-span-7 relative z-10">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1, ease: EASE }}
            className="font-display text-[11px] tracking-widest2 text-brass-deep uppercase mb-8"
          >
            Interior Design · Architecture
          </motion.p>

          <h1
            data-testid="hero-tagline-heading"
            className="font-serif font-medium text-5xl sm:text-7xl lg:text-[5.4rem] xl:text-[6.2rem] leading-[1.02] tracking-tight text-ink"
          >
            {LINES.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1 -mb-1">
                <motion.span
                  className="block"
                  initial={{ y: "112%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 1.15, delay: 0.25 + i * 0.13, ease: EASE }}
                >
                  {line.map((seg, j) =>
                    seg.italic ? (
                      <em key={j} className="italic font-light text-brass-deep">{seg.t}</em>
                    ) : (
                      <span key={j}>{seg.t}</span>
                    )
                  )}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            data-testid="hero-subheadline"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.85, ease: EASE }}
            className="mt-8 max-w-xl text-[15px] lg:text-base leading-relaxed text-taupe"
          >
            The Studio31 crafts warm, functional luxury for apartments, residences &amp; commercial
            spaces — 19 years of Meenu Gaur's signature detailing, from first sketch to turnkey
            handover.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1, ease: EASE }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-explore-knights-court-btn"
              onClick={() => scrollToId("knights-court")}
              className="group bg-ink text-cream px-8 py-4 text-sm tracking-wide flex items-center gap-2 hover:bg-brass hover:text-ink transition-colors duration-300"
            >
              Explore Knights Court
              <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
            <button
              data-testid="hero-consultation-btn"
              onClick={() => scrollToId("contact")}
              className="border border-brass/60 text-ink px-8 py-4 text-sm tracking-wide hover:border-brass hover:bg-brass/10 transition-all duration-300"
            >
              Book a Complimentary Consultation
            </button>
          </motion.div>
        </motion.div>

        <motion.div style={{ y: yImg }} className="lg:col-span-5 relative mt-4 lg:mt-0 mb-10 lg:mb-0">
          <div className="relative grain overflow-hidden">
            <motion.img
              src="/images/drawing-room-1.jpg"
              alt="Knights Court drawing room, designed by The Studio31"
              data-testid="hero-main-image"
              className="w-full h-[46vh] sm:h-[52vh] lg:h-[66vh] object-cover img-editorial"
              initial={{ scale: 1.18 }}
              animate={{ scale: 1 }}
              transition={{ duration: 2.4, ease: EASE }}
            />
          </div>

          <motion.img
            src="/images/drawing-room-3.jpg"
            alt="Drawing room detail with brass accents"
            className="absolute -left-6 sm:-left-10 lg:-left-16 -bottom-12 w-32 sm:w-40 lg:w-52 aspect-[3/4] object-cover border-[6px] border-cream shadow-2xl hidden sm:block"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.2, ease: EASE }}
          />

          <motion.div
            data-testid="hero-badge-wicci"
            className="absolute top-6 -right-2 lg:-right-5 bg-cream/90 backdrop-blur border border-brass/30 px-5 py-4 shadow-lg"
            initial={{ opacity: 0, y: -16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.4, ease: EASE }}
          >
            <div className="flex items-center gap-2 text-brass-deep">
              <Award size={14} />
              <span className="font-display text-[9px] tracking-widest2 uppercase">WICCI Iconic Women Award</span>
            </div>
            <div className="font-serif text-xl mt-1.5 text-ink">2026</div>
          </motion.div>
        </motion.div>
      </div>

      <motion.div
        data-testid="hero-metrics-container"
        className="relative border-t border-hairline bg-cream/85 backdrop-blur-sm"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.5 }}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-2 lg:grid-cols-4">
          {METRICS.map((m, i) => (
            <div key={m.label} className={`py-6 px-2 lg:px-6 ${i > 0 ? "lg:border-l border-hairline" : ""} ${i % 2 === 1 ? "border-l border-hairline lg:border-l" : ""}`}>
              <div className="font-serif text-2xl lg:text-3xl text-ink">{m.value}</div>
              <div className="text-[10.5px] tracking-wider text-taupe uppercase mt-1.5">{m.label}</div>
            </div>
          ))}
        </div>
      </motion.div>
    </section>
  );
}
